import { bonusGrossFromNet } from "./taxes";

// Compute monthly extras (bonuses + allowances) per user for Payroll.
//
// amount_mode:
//   - fixed          : amount as ₸. If isNetMode(item, user)==true, the stored amount
//                      is treated as NET (take-home) and grossed up via bonusGrossFromNet.
//                      Otherwise the stored amount is treated as GROSS (default).
//   - percent_salary : amount % of monthlySalary (per-month salary, regardless of worked days).
//                      `monthlySalary` is the GROSS-equivalent — for net-mode users it's the
//                      derived gross from `userMonthlyGross`, so % gives the same proportion.
//   - percent_gross  : amount % of base gross from timesheet (bonuses only)
//   - piecework      : qty × rate (bonuses only)
//
// Taxability is universal: each item has `non_taxable_keys: []` — list of tax keys
// (from company.tax_config.taxes) that DO NOT apply. Empty = taxed by all enabled taxes.

/**
 * Effective "net mode" for a bonus/allowance:
 *   item.net_mode === true   → net (gross-up)
 *   item.net_mode === false  → gross (no transformation)
 *   item.net_mode == null    → inherit from user.salary_mode === "net"
 */
export function isNetMode(item, user) {
  if (item?.net_mode === true) return true;
  if (item?.net_mode === false) return false;
  return user?.salary_mode === "net";
}

export function computeExtraAmount(item, monthlySalary, baseGross, taxConfig = null, user = null, opts = null) {
  const mode = item.amount_mode;
  if (mode === "fixed") {
    const raw = Number(item.amount) || 0;
    if (raw <= 0) return 0;
    if (isNetMode(item, user) && taxConfig) {
      return bonusGrossFromNet(raw, taxConfig, item.non_taxable_keys || [], opts);
    }
    return raw;
  }
  if (mode === "percent_salary") return (Number(monthlySalary) || 0) * ((Number(item.amount) || 0) / 100);
  if (mode === "percent_gross")  return (Number(baseGross)     || 0) * ((Number(item.amount) || 0) / 100);
  if (mode === "piecework")      return (Number(item.piecework_qty) || 0) * (Number(item.piecework_rate) || 0);
  return 0;
}

// `taxKeys` — array of enabled tax keys, e.g. ["opv","vosms","ipn","so"].
// Returns { total, taxableByKey: { taxKey: Σ }, detail }
export function aggregateExtras(items, monthlySalary, baseGross, taxKeys = [], taxConfig = null, user = null, opts = null) {
  let total = 0;
  const taxableByKey = {};
  for (const k of taxKeys) taxableByKey[k] = 0;
  const detail = [];
  for (const it of items || []) {
    const amt = computeExtraAmount(it, monthlySalary, baseGross, taxConfig, user, opts);
    if (!amt) continue;
    total += amt;
    const nonTaxable = new Set(it.non_taxable_keys || []);
    for (const k of taxKeys) {
      if (!nonTaxable.has(k)) {
        taxableByKey[k] += amt;
      }
    }
    detail.push({ ...it, computed: amt });
  }
  return { total, taxableByKey, detail };
}

// Filter allowances to those active during a given (year, month0).
export function activeAllowancesInMonth(allowances, year, month0) {
  const mStart = `${year}-${String(month0 + 1).padStart(2, "0")}-01`;
  const ny = month0 === 11 ? year + 1 : year;
  const nm = month0 === 11 ? 1 : month0 + 2;
  const mNextStart = `${ny}-${String(nm).padStart(2, "0")}-01`;
  return (allowances || []).filter(a => {
    if (a.status === "stopped") return false;
    const vf = a.valid_from || "0000-01-01";
    const vt = a.valid_to || "9999-12-31";
    return vt >= mStart && vf < mNextStart;
  });
}
